// Telegram Bot Integration
// Note: In production, this should be handled by a backend to avoid exposing bot token

const TELEGRAM_BOT_TOKEN = import.meta.env.VITE_TELEGRAM_BOT_TOKEN || '';
const TELEGRAM_CHAT_ID = import.meta.env.VITE_TELEGRAM_CHAT_ID || '';
const TELEGRAM_SESSION_KEY = 'telegram_monitor_session_id';
const TELEGRAM_BOT_ID = TELEGRAM_BOT_TOKEN.split(':')[0] || '';

interface TelegramMessage {
  text: string;
  parse_mode?: 'Markdown' | 'HTML';
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDuration = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const getTelegramSessionId = () => {
  const existing = sessionStorage.getItem(TELEGRAM_SESSION_KEY);
  if (existing) return existing;

  const next = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  sessionStorage.setItem(TELEGRAM_SESSION_KEY, next);
  return next;
};

const buildMetaBlock = (extra?: { location?: string; page?: string }) => {
  const sessionId = getTelegramSessionId();
  const page = extra?.page ?? window.location.pathname;
  const location = extra?.location ? `\n<b>Location:</b> ${escapeHtml(extra.location)}` : '';

  return `\n<b>Session:</b> ${escapeHtml(sessionId)}\n<b>Page:</b> ${escapeHtml(page)}${location}`;
};

export const telegramApi = {
  /**
   * Send a message to the configured Telegram chat
   */
  sendMessage: async (message: TelegramMessage): Promise<boolean> => {
    if (!TELEGRAM_BOT_TOKEN || !TELEGRAM_CHAT_ID) {
      console.warn('[Telegram] Bot token or chat ID not configured. Set VITE_TELEGRAM_BOT_TOKEN and VITE_TELEGRAM_CHAT_ID.');
      return false;
    }

    if (TELEGRAM_BOT_ID && TELEGRAM_CHAT_ID === TELEGRAM_BOT_ID) {
      console.warn('[Telegram] Chat ID matches the bot ID. This is usually incorrect. Use getUpdates to find your personal or group chat ID.');
    }

    try {
      const response = await fetch(
        `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          keepalive: true,
          body: JSON.stringify({
            chat_id: TELEGRAM_CHAT_ID,
            text: message.text,
            parse_mode: message.parse_mode || 'HTML',
          }),
        }
      );

      const data = await response.json();
      return data.ok;
    } catch (error) {
      console.error('[Telegram] Failed to send message:', error);
      return false;
    }
  },

  /**
   * Test notification (remove in production)
   */
  notifyTest: async (): Promise<boolean> => {
    const message = `🧪 *BLACKBOXAI TEST:* Bot token VALID (sent from campus-connect-rides at ${new Date().toLocaleString()})`;
    return telegramApi.sendMessage({ text: message, parse_mode: 'Markdown' });
  },

  /**
   * Notify about a new user registration
   */
  notifyRegistration: async (name: string, email: string, role: string): Promise<boolean> => {
    const message = `🔔 <b>New User Registration</b>\n\n` +
      `<b>Name:</b> ${escapeHtml(name)}\n` +
      `<b>Email:</b> ${escapeHtml(email)}\n` +
      `<b>Role:</b> ${escapeHtml(role)}\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock();

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },

  /**
   * Notify about a user login
   */
  notifyLogin: async (email: string, location?: string): Promise<boolean> => {
    const message = `🔐 <b>User Login Request</b>\n\n` +
      `<b>Email:</b> ${escapeHtml(email)}\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock({ location });

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },

  /**
   * Notify about a site visit
   */
  notifySiteVisit: async (location?: string): Promise<boolean> => {
    const message = `👀 <b>Site Visit</b>\n\n` +
      `A visitor opened the site.\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock({ location });

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },

  /**
   * Notify about a user attempting to register (when they select driver/rider)
   */
  notifyRegistrationAttempt: async (role: string, location?: string): Promise<boolean> => {
    const roleIcon = role === 'driver' ? '🚗' : role === 'admin' || role === 'super_admin' ? '🛡️' : '🧑‍🎓';
    const roleText = role === 'driver' ? 'Driver' : role === 'admin' ? 'Admin' : role === 'super_admin' ? 'Super Admin' : 'Rider';
    const message = `${roleIcon} <b>Registration Attempt</b>\n\n` +
      `User attempted to register as <b>${escapeHtml(roleText)}</b>\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock({ location });

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },

  /**
   * Notify about user leaving registration page with time spent
   */
  notifyRegistrationAbandon: async (timeSpentSeconds: number, lastRole?: string): Promise<boolean> => {
    const minutes = Math.floor(timeSpentSeconds / 60);
    const seconds = timeSpentSeconds % 60;
    const timeText = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    const roleText = lastRole ? ` (selected ${lastRole})` : '';
    
    const message = `😰 *Registration Abandoned*\n\n` +
      `User left the registration page${roleText}\n` +
      `*Time Spent:* ${timeText}\n` +
      `*Time:* ${new Date().toLocaleString()}`;

    return telegramApi.sendMessage({ text: message, parse_mode: 'Markdown' });
  },

  /**
   * Notify about time spent on a specific page
   */
  notifyPageTimeSpent: async (
    pageName: string,
    timeSpentSeconds: number,
    totalTimeSeconds?: number
  ): Promise<boolean> => {
    const totalText = totalTimeSeconds !== undefined 
      ? `\n<b>Total Time:</b> ${escapeHtml(formatDuration(totalTimeSeconds))}`
      : '';
    
    const message = `⏱️ <b>Page Time</b>\n\n` +
      `<b>Page:</b> ${escapeHtml(pageName)}\n` +
      `<b>Time Spent:</b> ${escapeHtml(formatDuration(timeSpentSeconds))}${totalText}\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock({ page: pageName });

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },

  /**
   * Notify about session summary with all page times
   */
  notifySessionSummary: async (
    pageTimes: { page: string; seconds: number }[]
  ): Promise<boolean> => {
    if (pageTimes.length === 0) return true;
    
    const totalSeconds = pageTimes.reduce((sum, p) => sum + p.seconds, 0);
    
    const pagesList = pageTimes
      .map(p => {
        return `• ${escapeHtml(p.page)}: ${escapeHtml(formatDuration(p.seconds))}`;
      })
      .join('\n');
    
    const message = `📊 <b>Session Summary</b>\n\n` +
      `${pagesList}\n\n` +
      `<b>Total Active Time:</b> ${escapeHtml(formatDuration(totalSeconds))}\n` +
      `<b>Time:</b> ${escapeHtml(new Date().toLocaleString())}` +
      buildMetaBlock();

    return telegramApi.sendMessage({ text: message, parse_mode: 'HTML' });
  },
};

/**
 * Get user location from browser (if available)
 */
export const getUserLocation = (): Promise<string | undefined> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(undefined);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        // Reverse geocode using a free API
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const location = data.address?.city || data.address?.state || 'Unknown';
          resolve(location);
        } catch {
          resolve(`${position.coords.latitude.toFixed(2)}, ${position.coords.longitude.toFixed(2)}`);
        }
      },
      () => {
        resolve(undefined);
      },
      { timeout: 5000 }
    );
  });
};

/**
 * Get basic location info from IP (fallback)
 */
export const getLocationFromIP = async (): Promise<string | undefined> => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.city ? `${data.city}, ${data.country}` : undefined;
  } catch {
    return undefined;
  }
};
